import React from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  Box,
  Button,
  Card,
  Icon,
  Separator,
  Spinner,
  Text,
} from "@salestrak/ui";
import { useWorkspace, type Workspace } from "#app/lib/data/workspace-context";
import { useAuth } from "#app/lib/auth-context";
import { readJWT } from "#app/lib/jwt";
import { WorkspaceAvatar } from "#app/components/brand/workspace-avatar";
import { WorkspaceScreenLayout } from "#app/components/workspace-screen-layout";

function WorkspaceRow({
  workspace,
  onJoin,
  onAccept,
  onDecline,
  isLast,
}: {
  workspace: Workspace;
  onJoin: () => void;
  onAccept: () => void;
  onDecline: () => void;
  isLast: boolean;
}) {
  const isActive = workspace.memberStatus === "active";
  const isInvited = workspace.memberStatus === "invited";

  const row = (
    <Box
      direction="row"
      wrap
      align="center"
      justify="end"
      gap="md"
      padding="md"
      minHeight={66}
    >
      <Box direction="row" align="center" gap="md" flex={1} minWidth={200}>
        <WorkspaceAvatar
          name={workspace.name}
          logo={workspace.settings?.logo}
          size="default"
        />
        <Box flex={1} gap="xs">
          <Text size="sm" weight="medium">
            {workspace.name}
          </Text>
          {workspace.memberCount != null && (
            <Text size="xs" variant="muted">
              {workspace.memberCount}{" "}
              {workspace.memberCount === 1 ? "member" : "members"}
            </Text>
          )}
        </Box>
      </Box>
      <Box direction="row" align="center" gap="xs">
        {isActive ? (
          <Icon name="chevron-right" size="2xl" color="muted" />
        ) : isInvited ? (
          <>
            <Button variant="outline" size="sm" onClick={onDecline}>
              Decline
            </Button>
            <Button variant="secondary" size="sm" onClick={onAccept}>
              Accept
            </Button>
          </>
        ) : (
          <Button variant="secondary" size="sm" onClick={onJoin}>
            Join
          </Button>
        )}
      </Box>
    </Box>
  );

  return (
    <Box>
      {isActive ? (
        <Box as="button" fill="width" onClick={onJoin}>
          {row}
        </Box>
      ) : (
        row
      )}
      {!isLast && (
        <Box paddingX="md">
          <Separator />
        </Box>
      )}
    </Box>
  );
}

function CreateWorkspaceRow({ onClick }: { onClick: () => void }) {
  return (
    <Card>
      <Box
        as="button"
        onClick={onClick}
        direction="row"
        align="center"
        gap="md"
        paddingX="md"
        minHeight={66}
      >
        <Box
          width={32}
          height={32}
          align="center"
          justify="center"
          rounded="lg"
          background="muted"
        >
          <Icon name="plus" size="md" color="muted" />
        </Box>
        <Box flex={1}>
          <Text size="sm" weight="medium">
            Create new workspace
          </Text>
        </Box>
        <Icon name="chevron-right" size="2xl" color="muted" />
      </Box>
    </Card>
  );
}

function WorkspaceSelectScreen() {
  const {
    workspaces,
    switchWorkspace,
    acceptInvitation,
    declineInvitation,
    isLoading,
    error,
  } = useWorkspace();

  const { state: authState, signOut } = useAuth();
  const navigate = useNavigate();

  const userEmail = React.useMemo(() => {
    if (!authState.accessToken) return null;
    const payload = readJWT(authState.accessToken);
    return (payload.email as string) ?? null;
  }, [authState.accessToken]);

  const title =
    isLoading || workspaces.length > 0
      ? "You have access to these workspaces"
      : "You do not have access to any workspaces";

  if (isLoading) {
    return (
      <Box fill="flex" align="center" justify="center">
        <Spinner color="muted" size="2xl" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box fill="flex" align="center" justify="center">
        <Text variant="destructive">{error}</Text>
      </Box>
    );
  }

  return (
    <WorkspaceScreenLayout email={userEmail}>
      {/* New Box carries no margin props — the trailing gap is padding on the
          heading block instead. */}
      <Box paddingBottom="2xl">
        <Text as="h3" size="2xl" weight="semibold" align="center">
          {title}
        </Text>
      </Box>

      <Box gap="md">
        {workspaces.length > 0 && (
          <Card>
            {workspaces.map((ws, index) => (
              <WorkspaceRow
                key={ws.workspaceId}
                workspace={ws}
                onJoin={async () => {
                  await switchWorkspace(ws.workspaceId);
                  navigate({ to: "/", replace: true });
                }}
                onAccept={async () => {
                  await acceptInvitation(ws.workspaceId);
                  navigate({ to: "/", replace: true });
                }}
                onDecline={() => declineInvitation(ws.workspaceId)}
                isLast={index === workspaces.length - 1}
              />
            ))}
          </Card>
        )}

        <CreateWorkspaceRow
          onClick={() => navigate({ to: "/workspace-create" })}
        />
      </Box>

      <Box align="start" paddingTop="lg">
        <Button variant="ghost" size="lg" onClick={signOut}>
          Back to login
        </Button>
      </Box>
    </WorkspaceScreenLayout>
  );
}

export const Route = createFileRoute("/(app)/workspace-select")({
  component: WorkspaceSelectScreen,
});
